/**
 * /reviews/{id} — PR review detail.
 *
 * Two render paths (mirrors InvestigationDetailPage):
 *   - Terminal (status='done'/'error'): fetch once, render directly from
 *     the persisted PrReviewDetail.
 *   - Non-terminal (status='queued'/'running'): open the catchup SSE
 *     stream and tail it; once it reaches a terminal status, refetch the
 *     full detail via REST so the findings table gets the real
 *     ReviewFindingPublic rows (the `review_submitted` SSE event only
 *     carries a narrower 9-key shape — see useReviewStream's docstring).
 *
 * Layout:
 *   1. Header: back link, ▸ REVIEW <id-prefix>, PR link, live pulse if
 *      streaming, status chip (+ superseded), verdict badge, posted
 *      indicator, relative time.
 *   2. error_message alert when status === 'error'.
 *   3. REPL accordion (ReplStream, reused as-is with actionLabel="review").
 *   4. Findings accordion — only once terminal; severity/category chips,
 *      path:line, anchored/outcome badges, expandable body_md + suggestion,
 *      👍/👎 + optional note, GitHub deep-link when posted.
 *   5. Footer telemetry strip — rounds · cost · tokens.
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { ReviewStatusChip, ReviewTriggerChip, ReviewVerdictBadge } from '@/components/ReviewBadges';
import { ReplStream, adaptToolCallsToTimeline } from '@/components/ReplStream';
import type { ReplStatus } from '@/components/ReplStream';
import type { TimelineEntry } from '@/components/InvestigationTimeline';
import { RetroAccordion } from '@/components/RetroAccordion';
import { NarrativeMarkdown } from '@/components/NarrativeMarkdown';
import { ApiError, reviews as reviewsApi } from '@/helpers/ApiHelper';
import { useReviewStream } from '@/hooks/useReviewStream';
import { formatRelative } from '@/utils/formatRelative';
import type {
  Category,
  FindingOutcome,
  PrReviewDetail,
  ReviewFindingPublic,
  ReviewStatus,
  Severity,
} from '@/types/review';

const NOTE_MAX_LENGTH = 2000;

function adaptStatus(s: ReviewStatus): ReplStatus {
  if (s === 'running') return 'streaming';
  if (s === 'done') return 'done';
  if (s === 'error') return 'error';
  return 'idle';
}

// ─── Live chip ────────────────────────────────────────────────────────────────

function LiveChip(): ReactElement {
  return (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      icon={
        <FiberManualRecordIcon
          sx={{
            fontSize: 12,
            animation: 'pulse 1.6s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.3 },
            },
          }}
        />
      }
      label="Live"
      aria-label="Review is streaming live"
    />
  );
}

// ─── Finding severity / category chips ─────────────────────────────────────

const SEVERITY_COLOR: Record<Severity, 'error' | 'warning' | 'info'> = {
  blocker: 'error',
  warning: 'warning',
  nit: 'info',
};

function SeverityChip({ severity }: { severity: Severity }): ReactElement {
  return (
    <Chip
      size="small"
      color={SEVERITY_COLOR[severity]}
      variant="filled"
      label={severity}
      aria-label={`severity: ${severity}`}
    />
  );
}

function CategoryChip({ category }: { category: Category }): ReactElement {
  return <Chip size="small" variant="outlined" label={category} />;
}

const OUTCOME_COLOR: Record<FindingOutcome, 'default' | 'success' | 'warning'> = {
  open: 'default',
  applied: 'success',
  resolved: 'success',
  dismissed: 'default',
  unaddressed: 'warning',
};

function OutcomeChip({ outcome }: { outcome: FindingOutcome }): ReactElement {
  return (
    <Chip
      size="small"
      variant={outcome === 'open' ? 'outlined' : 'filled'}
      color={OUTCOME_COLOR[outcome]}
      label={outcome}
    />
  );
}

function lineLabel(finding: ReviewFindingPublic): string {
  if (finding.start_line === null || finding.end_line === null) {
    return finding.end_line !== null ? `:${finding.end_line}` : '';
  }
  if (finding.start_line === finding.end_line) return `:${finding.end_line}`;
  return `:${finding.start_line}-${finding.end_line}`;
}

/**
 * `suggestion` is documented as raw replacement code (prompts/pr_review_v1.py:
 * "exact replacement code"), but models habitually wrap code in a markdown
 * fence anyway. Strip a single leading/trailing ``` fence (with an optional
 * language tag) so we don't render the literal backticks in the code block.
 */
function stripCodeFence(suggestion: string): string {
  const trimmed = suggestion.trim();
  const match = /^```[^\n]*\n([\s\S]*?)\n?```$/.exec(trimmed);
  return match ? (match[1] ?? '') : trimmed;
}

// ─── Per-finding feedback (thumb + optional note) ──────────────────────────

function FindingFeedback({
  reviewId,
  finding,
  onUpdated,
}: {
  reviewId: string;
  finding: ReviewFindingPublic;
  onUpdated: (updated: ReviewFindingPublic) => void;
}): ReactElement {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState(finding.feedback_note ?? '');
  const [noteExpanded, setNoteExpanded] = useState(Boolean(finding.feedback_note));

  const submit = async (score: 1 | -1, note: string | null): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await reviewsApi.submitFindingFeedback(reviewId, finding.id, {
        score,
        note,
      });
      onUpdated(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Could not save feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleThumb = (score: 1 | -1): void => {
    if (submitting || finding.feedback_score === score) return;
    void submit(score, finding.feedback_note);
  };

  const handleNoteBlur = (): void => {
    if (finding.feedback_score === null) return;
    const clipped = noteDraft.slice(0, NOTE_MAX_LENGTH);
    if ((finding.feedback_note ?? '') === clipped) return;
    void submit(finding.feedback_score as 1 | -1, clipped || null);
  };

  return (
    <Box role="group" aria-label={`Feedback for finding ${finding.id}`}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <IconButton
          aria-label="Mark this finding as useful"
          aria-pressed={finding.feedback_score === 1}
          size="large"
          onClick={() => handleThumb(1)}
          disabled={submitting}
          sx={{ color: finding.feedback_score === 1 ? 'success.main' : 'text.secondary' }}
        >
          {finding.feedback_score === 1 ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
        </IconButton>
        <IconButton
          aria-label="Mark this finding as not useful"
          aria-pressed={finding.feedback_score === -1}
          size="large"
          onClick={() => handleThumb(-1)}
          disabled={submitting}
          sx={{ color: finding.feedback_score === -1 ? 'error.main' : 'text.secondary' }}
        >
          {finding.feedback_score === -1 ? (
            <ThumbDownIcon fontSize="small" />
          ) : (
            <ThumbDownOutlinedIcon fontSize="small" />
          )}
        </IconButton>
        {finding.feedback_score !== null && !noteExpanded && (
          <Typography
            component="button"
            type="button"
            variant="body2"
            onClick={() => setNoteExpanded(true)}
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'primary.main',
              cursor: 'pointer',
              font: 'inherit',
              fontWeight: 500,
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            + Add a note
          </Typography>
        )}
        {submitting && (
          <Typography variant="caption" color="text.disabled" aria-live="polite">
            Saving…
          </Typography>
        )}
      </Stack>
      {error && (
        <Alert severity="error" role="alert" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {finding.feedback_score !== null && noteExpanded && (
        <TextField
          label="Note"
          id={`finding-feedback-note-${finding.id}`}
          placeholder="What worked, what didn't…"
          multiline
          minRows={1}
          maxRows={4}
          fullWidth
          size="small"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value.slice(0, NOTE_MAX_LENGTH))}
          onBlur={handleNoteBlur}
          inputProps={{ maxLength: NOTE_MAX_LENGTH }}
          helperText={`${noteDraft.length}/${NOTE_MAX_LENGTH} — saved when you click outside`}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
}

// ─── Finding row ────────────────────────────────────────────────────────────

function FindingRow({
  reviewId,
  repo,
  prNumber,
  finding,
  onUpdated,
}: {
  reviewId: string;
  repo: string;
  prNumber: number;
  finding: ReviewFindingPublic;
  onUpdated: (updated: ReviewFindingPublic) => void;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const githubUrl = finding.github_comment_id
    ? `https://github.com/${repo}/pull/${prNumber}#discussion_r${finding.github_comment_id}`
    : null;

  return (
    <Box sx={{ py: 1.5, px: 1.5, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minHeight: 32 }}>
        <SeverityChip severity={finding.severity} />
        <CategoryChip category={finding.category} />
        <Typography variant="body2" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
          {finding.path}
          {lineLabel(finding)}
        </Typography>
        {!finding.anchored && (
          <Tooltip title="Sherlock couldn't anchor this comment to the exact diff line">
            <Chip size="small" color="warning" variant="outlined" label="unanchored" />
          </Tooltip>
        )}
        <OutcomeChip outcome={finding.outcome} />
        <Box sx={{ flexGrow: 1 }} />
        {githubUrl && (
          <Tooltip title="Open on GitHub">
            <IconButton
              component="a"
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open this comment on GitHub"
              size="large"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          aria-label={expanded ? 'Collapse finding details' : 'Expand finding details'}
          size="large"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Collapse in={expanded} timeout={150} unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          <NarrativeMarkdown text={finding.body_md} />
          {finding.suggestion && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                suggestion
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {stripCodeFence(finding.suggestion)}
              </Box>
            </Box>
          )}
          <Box sx={{ mt: 1.5 }}>
            <FindingFeedback reviewId={reviewId} finding={finding} onUpdated={onUpdated} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ReviewDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<PrReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const stream = useReviewStream();

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    setError(null);
    reviewsApi
      .get(id)
      .then((resp) => {
        if (!active) return;
        setDetail(resp);
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError) {
          setError({ status: err.status, message: err.detail });
        } else {
          setError({ status: 0, message: err instanceof Error ? err.message : String(err) });
        }
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id || !detail) return;
    if (detail.status !== 'queued' && detail.status !== 'running') return;
    stream.start(id);
    return () => {
      stream.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, detail?.status]);

  useEffect(() => {
    if (!id) return;
    if (
      stream.state.status !== 'done' &&
      stream.state.status !== 'error' &&
      stream.state.status !== 'skipped'
    )
      return;
    if (!detail || detail.status === stream.state.status) return;
    reviewsApi
      .get(id)
      .then((resp) => setDetail(resp))
      .catch(() => {
        /* best-effort refetch */
      });
  }, [id, stream.state.status, detail]);

  const updateFinding = (updated: ReviewFindingPublic): void => {
    setDetail((prev) =>
      prev
        ? { ...prev, findings: prev.findings.map((f) => (f.id === updated.id ? updated : f)) }
        : prev,
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box role="status" aria-label="Loading review" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={2}>
          <Button component={RouterLink} to="/reviews" startIcon={<ArrowBackIcon />} sx={{ alignSelf: 'flex-start' }}>
            Back to reviews
          </Button>
          <Alert severity={error.status === 404 ? 'warning' : 'error'} role="alert">
            {error.message}
          </Alert>
        </Stack>
      </Container>
    );
  }

  if (!detail) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="warning" role="alert">
          No data
        </Alert>
      </Container>
    );
  }

  const isStreaming = stream.state.status === 'running';
  const displayStatus: ReviewStatus = isStreaming ? 'running' : detail.status;

  const timeline: TimelineEntry[] = isStreaming
    ? stream.state.timeline
    : detail.tool_calls.map((c, idx) => ({
        call_id: `r${c.round}-${idx}`,
        round: c.round,
        tool: c.tool,
        args: c.args,
        result_preview: c.result_preview,
        latency_ms: c.latency_ms,
        error: c.error,
      }));
  const summaryMd = isStreaming ? stream.state.summaryMd : detail.summary_md;
  const verdict = isStreaming ? stream.state.verdict : detail.verdict;
  const rounds = isStreaming ? stream.state.rounds || timeline.length : detail.rounds;
  const costUsd = isStreaming ? stream.state.costUsd : detail.cost_usd;
  const tokensUsed = isStreaming ? stream.state.tokensUsed : detail.tokens_used;
  const errorMessage = isStreaming ? null : detail.error_message;

  const shortId = detail.id.slice(0, 8).toUpperCase();
  const prUrl = `https://github.com/${detail.repo}/pull/${detail.pr_number}`;
  const githubReviewUrl =
    detail.posted && detail.github_review_id
      ? `${prUrl}#pullrequestreview-${detail.github_review_id}`
      : null;
  const showFindings = displayStatus === 'done' || displayStatus === 'error';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      <Stack spacing={{ xs: 1.5, sm: 2 }}>
        <Box>
          <Box sx={{ mb: 1 }}>
            <Button
              component={RouterLink}
              to="/reviews"
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', color: 'text.secondary' }}
            >
              ← reviews
            </Button>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} flexWrap="wrap" useFlexGap>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'primary.main',
              }}
            >
              ▸ REVIEW {shortId}
            </Typography>

            <Button
              component="a"
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ minHeight: 44, textTransform: 'none', fontFamily: 'monospace' }}
            >
              {detail.repo}#{detail.pr_number}
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {isStreaming && <LiveChip />}
            <ReviewStatusChip status={displayStatus} superseded={detail.superseded} />
            <ReviewVerdictBadge verdict={verdict} />
            <ReviewTriggerChip trigger={detail.trigger} />

            {githubReviewUrl ? (
              <Tooltip title="View the posted review on GitHub">
                <Chip
                  size="small"
                  component="a"
                  href={githubReviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  color="success"
                  variant="outlined"
                  label="posted"
                />
              </Tooltip>
            ) : (
              <Chip size="small" variant="outlined" label="not posted" sx={{ color: 'text.secondary' }} />
            )}

            <Typography variant="caption" color="text.secondary">
              {formatRelative(detail.created_at)}
            </Typography>
          </Stack>
        </Box>

        {displayStatus === 'error' && errorMessage && (
          <Alert severity="error" role="alert">
            {errorMessage}
          </Alert>
        )}

        <RetroAccordion title="REPL" defaultOpen={true}>
          <Box sx={{ pt: 1, pb: 1 }}>
            <ReplStream
              prompt={`${detail.repo}#${detail.pr_number}: ${detail.pr_title}`}
              anchor={null}
              timeline={adaptToolCallsToTimeline(timeline)}
              narrative={summaryMd}
              status={adaptStatus(displayStatus)}
              actionLabel="review"
            />
          </Box>
        </RetroAccordion>

        {showFindings && (
          <RetroAccordion title="FINDINGS" subtitle={`${detail.findings.length}`} defaultOpen={true}>
            <Box sx={{ pt: 1, pb: 1 }}>
              {detail.findings.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No findings — clean review.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                  {detail.findings.map((finding) => (
                    <FindingRow
                      key={finding.id}
                      reviewId={detail.id}
                      repo={detail.repo}
                      prNumber={detail.pr_number}
                      finding={finding}
                      onUpdated={updateFinding}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </RetroAccordion>
        )}

        <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={{ xs: 1.5, sm: 3 }} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            {[`rounds: ${rounds}`, `cost: $${costUsd.toFixed(4)}`, `tokens: ${tokensUsed.toLocaleString()}`].map(
              (label) => (
                <Typography
                  key={label}
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums' }}
                >
                  {label}
                </Typography>
              ),
            )}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
